// Aufgabenpfad und Badges -- alles aus dem lokalen Fortschritt (Sterne pro Aufgabe) abgeleitet.
// Bewusst ohne Zeit und ohne Vergleich mit anderen (wie GateLab, Anforderung F9).

import type { Task } from "./model.ts";

export interface Badge {
  id: string;
  icon: string; // kurzes Zeichen aus der Grundschrift (HTML), keine Emojis
  name: string;
  beschreibung: string;
}

type Stars = Record<string, number>;

interface BadgeRule extends Badge {
  /** null = Badge passt nicht zu diesen Aufgaben (wird dann nicht angezeigt) */
  earned: (tasks: Task[], stars: Stars) => boolean | null;
}

const solved = (stars: Stars, t: Task) => (stars[t.id] ?? 0) >= 1;
const countAtLeast = (tasks: Task[], stars: Stars, n: number) =>
  tasks.filter(t => (stars[t.id] ?? 0) >= n).length;
const allOf = (tasks: Task[], s: Stars, pick: (t: Task) => boolean) => {
  const sel = tasks.filter(pick);
  return sel.length ? sel.every(t => solved(s, t)) : null;
};

const RULES: BadgeRule[] = [
  {
    id: "erster-takt", icon: "1", name: "Erste Lösung",
    beschreibung: "Eine Aufgabe gelöst",
    earned: (tasks, s) => (tasks.length ? tasks.some(t => solved(s, t)) : null),
  },
  {
    id: "architekt", icon: "▦", name: "Architekt",
    beschreibung: "Alle Rechner zusammengebaut",
    earned: (tasks, s) => allOf(tasks, s, t => t.typ === "aufbau"),
  },
  {
    id: "busfahrer", icon: "⇄", name: "Busfahrer",
    beschreibung: "Alle Buszyklus-Aufgaben gelöst",
    earned: (tasks, s) => allOf(tasks, s, t => t.typ === "buszyklus"),
  },
  {
    id: "ohne-umweg", icon: "≤", name: "Ohne Umweg",
    beschreibung: "Drei Aufgaben auf dem kürzesten Weg gelöst",
    earned: (tasks, s) => (tasks.length >= 3 ? countAtLeast(tasks, s, 2) >= 3 : null),
  },
  {
    id: "ohne-spickzettel", icon: "★", name: "Ohne Spickzettel",
    beschreibung: "Drei Aufgaben mit allen drei Sternen gelöst",
    earned: (tasks, s) => (tasks.length >= 3 ? countAtLeast(tasks, s, 3) >= 3 : null),
  },
  {
    id: "alle-level", icon: "✓", name: "Alle Aufgaben",
    beschreibung: "Jede Aufgabe gelöst",
    earned: (tasks, s) => (tasks.length ? tasks.every(t => solved(s, t)) : null),
  },
  {
    id: "perfektionist", icon: "3★", name: "Perfektionist",
    beschreibung: "Jede Aufgabe mit drei Sternen gelöst",
    earned: (tasks, s) => (tasks.length ? countAtLeast(tasks, s, 3) === tasks.length : null),
  },
];

export interface BadgeState extends Badge { earned: boolean }

/** Alle zu diesen Aufgaben passenden Badges mit Status. */
export function badges(tasks: Task[], stars: Stars): BadgeState[] {
  const out: BadgeState[] = [];
  for (const { earned, ...badge } of RULES) {
    const e = earned(tasks, stars);
    if (e !== null) out.push({ ...badge, earned: e });
  }
  return out;
}

/** Badges, die durch den Wechsel von `before` zu `after` neu dazugekommen sind. */
export function newBadges(tasks: Task[], before: Stars, after: Stars): Badge[] {
  const had = new Set(badges(tasks, before).filter(b => b.earned).map(b => b.id));
  return badges(tasks, after).filter(b => b.earned && !had.has(b.id));
}

export type LevelState = "done" | "next" | "open";

/**
 * Status jeder Aufgabe im Pfad: gelöst; die erste ungelöste wird als „next“ vorgeschlagen.
 * Nichts ist gesperrt -- Aufgaben dürfen in beliebiger Reihenfolge gelöst werden.
 */
export function levels(tasks: Task[], stars: Stars): LevelState[] {
  let nextGiven = false;
  return tasks.map(t => {
    if (solved(stars, t)) return "done";
    if (!nextGiven) { nextGiven = true; return "next"; }
    return "open";
  });
}
